<template>
  <component :is="wrapperIs" v-bind="wrapperBind">
    <el-alert
      v-if="isEdit && quoteSnapshot && !quoteFlags.editable && !embedded"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom:16px;"
      :title="$t('quoteFormPage.lockedAlert')"
    />
    <p v-if="embedded && isEdit && quoteSnapshot && !quoteFlags.editable" class="quote-form__locked-hint">
      {{ $t('quoteFormPage.lockedAlert') }}
    </p>
    <el-form
      :label-width="embedded ? '92px' : '120px'"
      size="medium"
      :class="['quote-form', { 'quote-form--embedded': embedded }]"
    >
      <template v-if="!isEdit && !embedded">
        <el-divider content-position="left">{{ $t('quoteFormPage.sectionBase') }}</el-divider>
      </template>
      <el-form-item :label="$t('quoteFormPage.lead')" required>
        <el-select
          v-model="form.leadId"
          :disabled="isEdit || formLocked"
          filterable
          clearable
          :placeholder="$t('quoteFormPage.leadPh')"
          :loading="leadLoading"
          style="width:100%;"
          @change="onLeadIdChange"
        >
          <el-option
            v-for="o in leadOptions"
            :key="o.id"
            :label="formatLeadLabel(o)"
            :value="o.id"
          />
        </el-select>
        <p v-if="!leadLoading && !leadOptions.length" class="quote-form__opp-hint">
          {{ $t('quoteFormPage.leadEmptyHint') }}
        </p>
      </el-form-item>
      <el-row v-if="embedded" :gutter="16">
        <el-col :span="12">
          <el-form-item :label="$t('common.firstName')">
            <el-input v-model="form.customerFirstName" :placeholder="$t('quoteFormPage.firstNamePh')" clearable :disabled="formLocked" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('common.lastName')">
            <el-input v-model="form.customerLastName" :placeholder="$t('quoteFormPage.lastNamePh')" clearable :disabled="formLocked" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item v-else :label="$t('quoteFormPage.customerName')">
        <div class="quote-form__name-row">
          <el-input v-model="form.customerFirstName" :placeholder="$t('quoteFormPage.firstNamePh')" clearable :disabled="formLocked" />
          <el-input v-model="form.customerLastName" :placeholder="$t('quoteFormPage.lastNamePh')" clearable :disabled="formLocked" />
        </div>
      </el-form-item>
      <el-form-item :label="$t('quoteFormPage.validUntil')" required>
        <el-date-picker
          v-model="form.validUntil"
          type="date"
          value-format="yyyy-MM-dd"
          :placeholder="$t('quoteFormPage.validUntilPh')"
          :style="embedded ? { width: '100%' } : { width: '100%', maxWidth: '320px' }"
          :disabled="formLocked"
        />
      </el-form-item>
      <el-divider v-if="!embedded" content-position="left">{{ $t('quoteFormPage.sectionLines') }}</el-divider>
      <el-form-item label-width="0" class="quote-form__lines">
        <div class="quote-form__lines-toolbar" :class="{ 'quote-form__lines-toolbar--embedded': embedded }">
          <span v-if="embedded" class="quote-form__lines-title quote-form__lines-title--toolbar">{{ $t('quoteFormPage.sectionLines') }}</span>
          <span v-else class="quote-form__lines-hint">{{ $t('quoteFormPage.linesHint') }}</span>
          <el-button type="primary" plain size="small" :disabled="formLocked" @click="addLine">{{ $t('quoteFormPage.addLine') }}</el-button>
        </div>
        <el-table :data="lines" border stripe class="quote-form__table">
          <el-table-column :label="$t('quoteFormPage.colProduct')" min-width="190">
            <template slot-scope="s">
              <el-select
                v-model="s.row.productId"
                filterable
                clearable
                size="small"
                :loading="productLoading"
                :placeholder="$t('quoteFormPage.productPh')"
                :disabled="formLocked"
                style="width:100%;"
                popper-class="quote-form__product-select-dropdown"
                @focus="captureProductIdBeforeSelect(s.row)"
                @change="id => onProductLineChange(s.row, id)"
              >
                <el-option
                  key="__quote_create_product__"
                  class="quote-form__opt-create"
                  :label="$t('quoteFormPage.addNewProduct')"
                  :value="productCreateSentinel"
                >
                  <span class="quote-form__product-opt-title quote-form__product-opt-title--create">
                    {{ $t('quoteFormPage.addNewProduct') }}
                  </span>
                </el-option>
                <el-option
                  v-for="o in productOptions"
                  :key="o.id"
                  :label="formatProductOptionPrimary(o)"
                  :value="Number(o.id)"
                >
                  <div class="quote-form__product-opt">
                    <span class="quote-form__product-opt-title">{{ formatProductLabel(o) }}</span>
                    <span v-if="productOptionDescPreview(o)" class="quote-form__product-opt-desc">
                      {{ productOptionDescPreview(o) }}
                    </span>
                  </div>
                </el-option>
              </el-select>
              <p v-if="!s.row.productId && s.row.itemName" class="quote-form__legacy-item">
                {{ $t('quoteFormPage.legacyLineItem') }}{{ s.row.itemName }}
              </p>
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colDescription')" min-width="260">
            <template slot-scope="s">
              <el-input
                v-model="s.row.detailText"
                type="textarea"
                :autosize="{ minRows: 3, maxRows: 12 }"
                size="small"
                :placeholder="$t('quoteFormPage.descriptionPh')"
                :disabled="formLocked"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colQty')" width="100">
            <template slot-scope="s">
              <el-input
                v-model="s.row.qty"
                size="small"
                :disabled="formLocked"
                @input="v => onDecimalInput(s.row, 'qty', v)"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colUnitUsd')" width="120">
            <template slot-scope="s">
              <el-input
                v-model="s.row.unitPrice"
                size="small"
                :disabled="formLocked"
                @input="v => onDecimalInput(s.row, 'unitPrice', v)"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colTaxPct')" width="90">
            <template slot-scope="s">
              <el-input
                v-model="s.row.taxRate"
                size="small"
                :disabled="formLocked"
                @input="v => onDecimalInput(s.row, 'taxRate', v)"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colLineTotal')" width="120" align="right">
            <template slot-scope="s">{{ formatUsd(lineAmount(s.row)) }}</template>
          </el-table-column>
          <el-table-column label="" width="72" align="center">
            <template slot-scope="s">
              <el-button type="text" class="quote-form__del" :disabled="formLocked" @click="lines.splice(s.$index, 1)">{{ $t('quoteFormPage.removeLine') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
        <p v-if="lines.length" class="quote-form__total">{{ $t('quoteFormPage.estimatedTotal') }}{{ formatUsd(estimatedTotal) }}</p>
      </el-form-item>
      <el-divider v-if="!embedded" content-position="left">{{ $t('quoteFormPage.sectionPaymentPlan') }}</el-divider>
      <el-form-item label-width="0" class="quote-form__payment-plans">
        <div class="quote-form__lines-toolbar" :class="{ 'quote-form__lines-toolbar--embedded': embedded }">
          <span v-if="embedded" class="quote-form__lines-title quote-form__lines-title--toolbar">{{ $t('quoteFormPage.sectionPaymentPlan') }}</span>
          <span v-else class="quote-form__lines-hint">{{ $t('quoteFormPage.paymentPlanHint') }}</span>
          <el-button type="primary" plain size="small" :disabled="formLocked" @click="addPaymentPlan">{{ $t('quoteFormPage.addPlanNode') }}</el-button>
        </div>
        <div v-if="paymentPlans.length" class="quote-form__plan-stats">
          <span>{{ $t('quoteFormPage.planPlannedTotal') }} {{ formatUsd(planPlannedTotal) }}</span>
          <span v-if="estimatedTotal > 0" class="quote-form__plan-unplanned">{{ $t('quoteFormPage.planUnplanned') }} {{ formatUsd(planUnplannedAmount) }}</span>
        </div>
        <el-table :data="paymentPlans" border stripe class="quote-form__table quote-form__plan-table" :empty-text="$t('quoteFormPage.emptyPaymentPlans')">
          <el-table-column :label="$t('quoteFormPage.colPlanNode')" min-width="140">
            <template slot-scope="s">
              <el-input v-model="s.row.nodeName" size="small" :placeholder="$t('quoteFormPage.planNodePh')" :disabled="formLocked" clearable />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colPlanAmount')" width="130">
            <template slot-scope="s">
              <el-input
                v-model="s.row.plannedAmount"
                size="small"
                :disabled="formLocked"
                :placeholder="$t('quoteFormPage.planAmountPh')"
                @input="v => onDecimalInput(s.row, 'plannedAmount', v)"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colPlanDate')" width="160">
            <template slot-scope="s">
              <el-date-picker
                v-model="s.row.plannedDate"
                type="date"
                value-format="yyyy-MM-dd"
                size="small"
                :disabled="formLocked"
                :placeholder="$t('quoteFormPage.planDatePh')"
                style="width:100%;"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('quoteFormPage.colPlanRemark')" min-width="160">
            <template slot-scope="s">
              <el-input v-model="s.row.remarkText" size="small" :disabled="formLocked" clearable />
            </template>
          </el-table-column>
          <el-table-column label="" width="72" align="center">
            <template slot-scope="s">
              <el-button type="text" class="quote-form__del" :disabled="formLocked" @click="paymentPlans.splice(s.$index, 1)">{{ $t('quoteFormPage.removeLine') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form-item>
      <template v-if="showQuoteProjectAttachments">
        <el-divider content-position="left">{{ $t('quoteFormPage.sectionAttachments') }}</el-divider>
        <el-form-item label-width="0">
          <div v-loading="quoteAttachLoading" class="quote-form__attachments">
            <p v-if="!quoteAttachmentRows.length && !quotePendingAttachmentFiles.length" class="quote-form__opp-hint">{{ $t('quoteFormPage.attachEmpty') }}</p>
            <ul v-else class="quote-form__att-list">
              <li v-for="row in quoteAttachmentRows" :key="'qa' + row.id">
                <span class="quote-form__att-name">{{ row.fileName || '—' }}</span>
                <el-button type="text" size="small" @click="previewQuoteAttachment(row)">{{ $t('quoteFormPage.attachView') }}</el-button>
                <el-button type="text" size="small" @click="downloadQuoteAttachment(row)">{{ $t('quoteFormPage.attachDownload') }}</el-button>
              </li>
              <li v-for="(file, idx) in quotePendingAttachmentFiles" :key="'pending-' + idx + '-' + file.name">
                <span class="quote-form__att-name">{{ file.name }}</span>
                <el-button type="text" size="small" @click="previewPendingQuoteAttachment(idx)">{{ $t('quoteFormPage.attachView') }}</el-button>
              </li>
            </ul>
            <div v-if="!embedded" class="quote-form__att-upload">
              <input
                ref="quoteAttachFileInput"
                type="file"
                class="hidden-file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip"
                @change="onQuoteAttachFile"
              />
              <el-button size="small" icon="el-icon-upload2" @click="$refs.quoteAttachFileInput && $refs.quoteAttachFileInput.click()">{{ $t('common.upload') }}</el-button>
            </div>
          </div>
        </el-form-item>
      </template>
      <el-form-item
        v-if="isEdit && quoteSnapshot && Number(quoteSnapshot.quoteStatusCode) === 5 && !embedded"
        label-width="0"
        class="quote-form__contract-row"
      >
        <el-button type="warning" plain size="small" @click="openContractFromQuote">
          {{ $t('quoteFormPage.createContractFromQuote') }}
        </el-button>
      </el-form-item>
      <el-form-item v-if="!embedded" class="quote-form__actions">
        <el-button @click="onCancelFullPage">{{ $t('common.cancel') }}</el-button>
        <el-button
          v-if="!formLocked"
          type="success"
          plain
          :loading="saving"
          @click="submitForApproval"
        >{{ $t('quoteFormPage.saveSubmitApproval') }}</el-button>
        <el-button
          v-if="!formLocked"
          type="primary"
          :loading="saving"
          @click="save"
        >{{ $t('quoteFormPage.saveDraft') }}</el-button>
        <el-button
          v-if="quoteDeletable"
          type="danger"
          plain
          :loading="saving"
          @click="deleteDraft"
        >{{ $t('quoteFormPage.deleteDraft') }}</el-button>
      </el-form-item>
    </el-form>

    <RecordFormDialog
      :visible.sync="productQuickCreateVisible"
      :title="$t('productFormPage.newTitle')"
      @closed="onQuickProductDialogClosed"
    >
      <ProductForm
        v-if="productQuickCreateVisible"
        :key="quickProductFormKey"
        ref="quickProductForm"
        embedded
        @saved="onQuickProductSaved"
        @saving="v => (productQuickCreateSaving = v)"
      />
      <template slot="footer">
        <el-button @click="productQuickCreateVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="productQuickCreateSaving" @click="submitQuickProductDialog">{{ $t('common.save') }}</el-button>
      </template>
    </RecordFormDialog>
  </component>
</template>

<script>
import {
  deleteQuote,
  downloadQuoteAttachmentUrl,
  getQuoteDetail,
  listQuoteAttachments,
  previewQuoteAttachmentUrl,
  saveQuote,
  submitQuote,
  updateQuote,
  uploadQuoteAttachment
} from '@/api/quote'
import { getLeadDetail, getLeadPage } from '@/api/lead'
import { getProduct, getProductOptions } from '@/api/product'
import { quoteUiFlags } from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { downloadBlobAuthenticated, previewBlobAuthenticated } from '@/utils/downloadBlobAuth'
import FormPageShell from '@/components/FormPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import ProductForm from '@/views/product/ProductForm.vue'

/** First dropdown row: open quick-create product dialog (not a real product id). */
const PRODUCT_CREATE_SENTINEL = '__QUOTE_CREATE_PRODUCT__'

export default {
  name: 'QuoteForm',
  components: { FormPageShell, RecordFormDialog, ProductForm },
  props: {
    embedded: { type: Boolean, default: false },
    recordId: { type: [String, Number], default: null },
    /** Pre-select lead when creating from another screen (e.g. lead detail). */
    initialLeadId: { type: [String, Number], default: null }
  },
  data() {
    return {
      isEdit: false,
      saving: false,
      quoteSnapshot: null,
      form: { leadId: '', customerFirstName: '', customerLastName: '', validUntil: '' },
      leadOptions: [],
      leadLoading: false,
      productOptions: [],
      productLoading: false,
      lines: [],
      paymentPlans: [],
      quoteAttachLoading: false,
      quoteAttachments: [],
      quotePendingAttachmentFiles: [],
      productCreateSentinel: PRODUCT_CREATE_SENTINEL,
      productQuickCreateVisible: false,
      productQuickCreateSaving: false,
      quickProductFormKey: 0,
      quickAddProductRow: null
    }
  },
  computed: {
    wrapperIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    wrapperBind() {
      if (this.embedded) {
        return { class: 'quote-form__embed' }
      }
      return {
        isEdit: this.isEdit,
        title: this.isEdit ? this.$t('quoteFormPage.editTitle') : this.$t('quoteFormPage.newTitle'),
        subtitle: this.isEdit ? '' : this.$t('quoteFormPage.newSubtitle'),
        icon: 'el-icon-document',
        theme: 'primary',
        wide: true
      }
    },
    quoteFlags() {
      return this.quoteSnapshot ? quoteUiFlags(this.quoteSnapshot) : { editable: true, canSubmit: false, canApprove: false, canSend: false }
    },
    formLocked() {
      return this.isEdit && !this.quoteFlags.editable
    },
    /** 与后端可删除条件一致：内部未提交/已拒绝，且未进入已发送等状态 */
    quoteDeletable() {
      if (!this.isEdit || !this.quoteSnapshot) return false
      const qs = this.quoteSnapshot.quoteStatusCode
      const qsn = qs != null && qs !== '' ? Number(qs) : null
      if (qsn != null && Number.isFinite(qsn) && qsn >= 4) return false
      const ic = this.quoteSnapshot.internalApprovalCode
      const icn = ic != null && ic !== '' ? Number(ic) : null
      if (icn === null) return true
      return icn === 0 || icn === 3
    },
    estimatedTotal() {
      return this.lines.reduce((sum, l) => sum + this.lineAmount(l), 0)
    },
    planPlannedTotal() {
      return (this.paymentPlans || []).reduce((acc, p) => acc + (Number(p.plannedAmount) || 0), 0)
    },
    planUnplannedAmount() {
      const diff = this.estimatedTotal - this.planPlannedTotal
      return diff > 0 ? diff : 0
    },
    showQuoteProjectAttachments() {
      return !this.embedded
    },
    quoteAttachmentRows() {
      return this.quoteAttachments || []
    }
  },
  watch: {
    isEdit() {
      this.scheduleEmbeddedFooterEmit()
    },
    quoteSnapshot: {
      handler(v) {
        this.scheduleEmbeddedFooterEmit()
        if (v && v.id) {
          this.loadQuoteAttachments(v.id)
        } else {
          this.quoteAttachments = []
        }
      },
      deep: true
    }
  },
  async created() {
    if (this.embedded) {
      const rid = this.recordId
      this.isEdit = rid != null && rid !== ''
      if (this.initialLeadId != null && this.initialLeadId !== '') {
        this.form.leadId = Number(this.initialLeadId)
      }
      if (this.isEdit) {
        try {
          const r = await getQuoteDetail(rid)
          const d = r.data !== undefined ? r.data : r
          const q = d.quote
          this.quoteSnapshot = q
          this.form = {
            leadId: q.leadId != null && q.leadId !== '' ? Number(q.leadId) : '',
            customerFirstName: q.customerFirstName,
            customerLastName: q.customerLastName,
            validUntil: q.validUntil
          }
          this.lines = (d.details || []).map(d => ({
            productId: d.productId != null && d.productId !== '' ? Number(d.productId) : '',
            itemName: d.itemName,
            detailText: d.detailText || '',
            qty: d.qty,
            unitPrice: d.unitPrice,
            taxRate: d.taxRate
          }))
          this.paymentPlans = this.mapPaymentPlansFromApi(d.paymentPlans)
          await this.loadQuoteAttachments(q.id)
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('common.loadFailed'))
          this.$emit('cancel')
          return
        }
      } else if (!this.lines.length) {
        this.addLine()
      }
      await this.loadLeadOptions()
      await this.loadProductOptions()
      if (!this.isEdit) {
        await this.applyCustomerNamesFromLead(this.form.leadId)
      }
      this.emitEmbeddedFooterState()
      return
    }

    const id = this.$route.params.id
    const qid = this.$route.query.leadId
    if (qid) this.form.leadId = Number(qid)
    if (id) {
      this.isEdit = true
      const r = await getQuoteDetail(id)
      const d = r.data !== undefined ? r.data : r
      const q = d.quote
      this.quoteSnapshot = q
      this.form = {
        leadId: q.leadId != null && q.leadId !== '' ? Number(q.leadId) : '',
        customerFirstName: q.customerFirstName,
        customerLastName: q.customerLastName,
        validUntil: q.validUntil
      }
      this.lines = (d.details || []).map(d => ({
        productId: d.productId != null && d.productId !== '' ? Number(d.productId) : '',
        itemName: d.itemName,
        detailText: d.detailText || '',
        qty: d.qty,
        unitPrice: d.unitPrice,
        taxRate: d.taxRate
      }))
      this.paymentPlans = this.mapPaymentPlansFromApi(d.paymentPlans)
      await this.loadQuoteAttachments(this.quoteSnapshot.id)
    } else if (!this.lines.length) {
      this.addLine()
    }
    await this.loadLeadOptions()
    await this.loadProductOptions()
    if (!this.isEdit) {
      await this.applyCustomerNamesFromLead(this.form.leadId)
    }
  },
  methods: {
    async loadQuoteAttachments(quoteId) {
      const qid = quoteId || (this.quoteSnapshot && this.quoteSnapshot.id)
      if (!qid) {
        this.quoteAttachments = []
        return
      }
      this.quoteAttachLoading = true
      try {
        const r = await listQuoteAttachments(qid)
        this.quoteAttachments = r.data !== undefined ? r.data : r
      } catch (e) {
        this.quoteAttachments = []
      } finally {
        this.quoteAttachLoading = false
      }
    },
    async previewQuoteAttachment(row) {
      if (!row || typeof row.id !== 'number') return
      const name = (row.fileName != null && String(row.fileName).trim()) || 'file.bin'
      try {
        await previewBlobAuthenticated(previewQuoteAttachmentUrl(row.id), name)
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    async downloadQuoteAttachment(row) {
      if (!row || typeof row.id !== 'number') return
      const name = (row.fileName != null && String(row.fileName).trim()) || 'file.bin'
      try {
        await downloadBlobAuthenticated(downloadQuoteAttachmentUrl(row.id), name)
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    onQuoteAttachFile(e) {
      const picked = Array.from((e.target && e.target.files) || [])
      if (e.target) e.target.value = ''
      this.quotePendingAttachmentFiles = this.quotePendingAttachmentFiles.concat(picked)
    },
    previewPendingQuoteAttachment(index) {
      const f = this.quotePendingAttachmentFiles[index]
      if (!f) return
      const url = window.URL.createObjectURL(f)
      window.open(url, '_blank', 'noopener')
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000)
    },
    async uploadPendingQuoteAttachments(quoteId) {
      if (!quoteId || !this.quotePendingAttachmentFiles.length) return
      for (const file of this.quotePendingAttachmentFiles) {
        await uploadQuoteAttachment(quoteId, file)
      }
      this.quotePendingAttachmentFiles = []
    },
    scheduleEmbeddedFooterEmit() {
      if (!this.embedded) return
      this.$nextTick(() => this.emitEmbeddedFooterState())
    },
    emitEmbeddedFooterState() {
      if (!this.embedded) return
      const qs5 =
        this.isEdit &&
        this.quoteSnapshot &&
        Number(this.quoteSnapshot.quoteStatusCode) === 5
      const ac = qs5 && this.quoteSnapshot.activeContractId != null && this.quoteSnapshot.activeContractId !== ''
        ? Number(this.quoteSnapshot.activeContractId)
        : null
      this.$emit('embedded-footer', {
        formLocked: this.formLocked,
        showDeleteDraft: this.quoteDeletable,
        showCreateContractFromQuote: qs5 && !ac,
        viewContractId: ac
      })
    },
    onCancelFullPage() {
      this.$router.back()
    },
    onLeadIdChange(leadId) {
      this.applyCustomerNamesFromLead(leadId)
    },
    async applyCustomerNamesFromLead(leadId) {
      if (this.isEdit || this.formLocked) return
      const nid = leadId != null && leadId !== '' ? Number(leadId) : NaN
      if (!Number.isFinite(nid)) return
      await this.ensureLeadInOptions(nid)
      const lead = this.leadOptions.find(x => Number(x.id) === nid)
      if (!lead) return
      this.form.customerFirstName = lead.firstName != null ? String(lead.firstName) : ''
      this.form.customerLastName = lead.lastName != null ? String(lead.lastName) : ''
    },
    async deleteDraft() {
      if (!this.quoteDeletable) return
      const id = this.quoteEditId()
      if (id == null || id === '') return
      try {
        await this.$confirm(this.$t('quoteFormPage.confirmDeleteDraft'), this.$t('common.tip'), {
          type: 'warning',
          confirmButtonText: this.$t('common.delete'),
          cancelButtonText: this.$t('common.cancel')
        })
      } catch (e) {
        return
      }
      this.saving = true
      this.$emit('saving', true)
      try {
        await deleteQuote(id)
        this.$message.success(this.$t('quoteFormPage.deletedDraft'))
        if (this.embedded) {
          this.$emit('saved')
        } else {
          this.$router.push('/quote/list')
        }
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteFormPage.deleteDraftFailed'))
      } finally {
        this.saving = false
        this.$emit('saving', false)
      }
    },
    openContractFromQuote() {
      const id = this.quoteEditId()
      if (id == null || id === '') return
      if (this.embedded) {
        this.$emit('create-contract-from-quote', { quoteId: id })
        return
      }
      this.$router.push({ path: '/contract/from-quote', query: { quoteId: String(id) } })
    },
    quoteEditId() {
      if (this.embedded) return this.recordId
      return this.$route.params.id
    },
    formatUsd,
    lineAmount(row) {
      const qty = Number(row.qty) || 0
      const unit = Number(row.unitPrice) || 0
      const markupPct = Number(row.taxRate) || 0
      const base = qty * unit
      const markupAmt = base * (markupPct / 100)
      return Math.round((base + markupAmt) * 100) / 100
    },
    formatLeadLabel(o) {
      if (!o) return ''
      const no = (o.leadNo || '').trim()
      const fn = (o.firstName || '').trim()
      const ln = (o.lastName || '').trim()
      const person = [fn, ln].filter(Boolean).join(' ').trim()
      if (no && person) return `${no} · ${person}`
      return person || no || this.$t('quoteFormPage.leadNum', { id: o.id })
    },
    formatProductLabel(o) {
      if (!o) return ''
      const code = (o.productCode || '').trim()
      const name = (o.productName || '').trim()
      if (code && name) return `${code} · ${name}`
      return name || code || this.$t('quoteFormPage.productNum', { id: o.id })
    },
    /** Single-line label for filter/matched display (includes description snippet). */
    formatProductOptionPrimary(o) {
      const primary = this.formatProductLabel(o)
      const desc = this.productOptionDescPreview(o)
      return desc ? `${primary} — ${desc}` : primary
    },
    productOptionDescPreview(o) {
      const raw = o && o.description != null ? String(o.description).trim() : ''
      if (!raw) return ''
      const max = 120
      return raw.length > max ? `${raw.slice(0, max)}…` : raw
    },
    captureProductIdBeforeSelect(row) {
      const cur = row.productId
      row._productSelectPrevId =
        cur === this.productCreateSentinel || cur === '' || cur == null ? '' : Number(cur)
    },
    onProductLineChange(row, id) {
      if (id === this.productCreateSentinel) {
        this.quickAddProductRow = row
        let prev = row._productSelectPrevId
        if (prev === this.productCreateSentinel || prev == null || prev === '') prev = ''
        else prev = Number(prev)
        this.$nextTick(() => {
          this.$set(row, 'productId', prev === '' ? '' : prev)
        })
        this.quickProductFormKey += 1
        this.productQuickCreateVisible = true
        return
      }
      void this.applyProductToLine(row, id)
    },
    submitQuickProductDialog() {
      const ref = this.$refs.quickProductForm
      if (ref && typeof ref.submit === 'function') ref.submit()
    },
    onQuickProductDialogClosed() {
      this.quickAddProductRow = null
    },
    async onQuickProductSaved(payload) {
      const newId = payload && payload.id != null ? Number(payload.id) : NaN
      const row = this.quickAddProductRow
      this.productQuickCreateVisible = false
      this.quickAddProductRow = null
      await this.loadProductOptions()
      if (row && Number.isFinite(newId)) {
        await this.applyProductToLine(row, newId)
      }
    },
    async applyProductToLine(row, id) {
      if (!id) {
        row.productId = ''
        row.itemName = ''
        row.unitPrice = 0
        return
      }
      const nid = Number(id)
      if (!Number.isFinite(nid)) {
        return
      }
      row.productId = nid
      try {
        const r = await getProduct(nid)
        const p = r && r.data !== undefined ? r.data : r
        if (p && p.id != null) {
          row.productId = Number(p.id)
          row.itemName = p.productName || ''
          row.unitPrice = p.unitPrice != null ? p.unitPrice : 0
          const desc = p.description != null ? String(p.description).trim() : ''
          this.$set(row, 'detailText', desc)
          return
        }
      } catch (e) {
        /* fall back to options cache */
      }
      const hit = this.productOptions.find(p => Number(p.id) === nid)
      if (!hit) return
      row.productId = Number(hit.id)
      row.itemName = hit.productName || ''
      row.unitPrice = hit.unitPrice != null ? hit.unitPrice : 0
      const desc = hit.description != null ? String(hit.description).trim() : ''
      this.$set(row, 'detailText', desc)
    },
    sanitizeDecimal(value) {
      let s = String(value == null ? '' : value).replace(/[^\d.]/g, '')
      const firstDot = s.indexOf('.')
      if (firstDot !== -1) {
        s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '')
        const parts = s.split('.')
        s = `${parts[0]}.${(parts[1] || '').slice(0, 2)}`
      }
      return s
    },
    onDecimalInput(row, field, value) {
      this.$set(row, field, this.sanitizeDecimal(value))
    },
    async ensureLeadInOptions(id) {
      const nid = Number(id)
      if (!nid || this.leadOptions.some(x => Number(x.id) === nid)) return
      try {
        const r = await getLeadDetail(nid)
        const raw = r.data !== undefined ? r.data : r
        const lead = raw.lead || raw
        if (lead && lead.id) this.leadOptions.unshift(lead)
      } catch (e) {
        /* ignore */
      }
    },
    async loadLeadOptions() {
      this.leadLoading = true
      try {
        const r = await getLeadPage({ pageNo: 1, pageSize: 500 })
        const d = r.data !== undefined ? r.data : r
        this.leadOptions = d.records || []
        if (this.form.leadId) {
          await this.ensureLeadInOptions(this.form.leadId)
        }
      } catch (e) {
        this.leadOptions = []
        this.$message.error((e && e.message) || this.$t('quoteFormPage.loadLeadFailed'))
      } finally {
        this.leadLoading = false
      }
    },
    async loadProductOptions() {
      this.productLoading = true
      try {
        const r = await getProductOptions({ limit: 500 })
        const d = r.data !== undefined ? r.data : r
        this.productOptions = d || []
      } catch (e) {
        this.productOptions = []
        this.$message.error((e && e.message) || this.$t('quoteFormPage.loadProductsFailed'))
      } finally {
        this.productLoading = false
      }
    },
    addLine() {
      this.lines.push({ productId: '', itemName: '', detailText: '', qty: 1, unitPrice: 0, taxRate: 0 })
    },
    addPaymentPlan() {
      this.paymentPlans.push({ nodeName: '', plannedAmount: '', plannedDate: '', remarkText: '' })
    },
    mapPaymentPlansFromApi(rows) {
      if (!Array.isArray(rows)) return []
      return rows.map(p => ({
        nodeName: p.nodeName || '',
        plannedAmount: p.plannedAmount != null ? String(p.plannedAmount) : '',
        plannedDate: p.plannedDate || '',
        remarkText: p.remarkText || ''
      }))
    },
    buildPaymentPlans() {
      return (this.paymentPlans || []).map(p => ({
        nodeName: p.nodeName || null,
        plannedAmount: p.plannedAmount !== '' && p.plannedAmount != null ? Number(p.plannedAmount) : null,
        plannedDate: p.plannedDate || null,
        remarkText: p.remarkText || null
      }))
    },
    buildDetails() {
      return this.lines.map(l => ({
        productId: l.productId || null,
        itemName: l.itemName,
        detailText: l.detailText,
        qty: Number(l.qty) || 0,
        unitPrice: Number(l.unitPrice) || 0,
        taxRate: Number(l.taxRate) || 0
      }))
    },
    finishSaveNavigation() {
      if (this.embedded) {
        this.$emit('saved')
      } else {
        this.$router.push('/quote/list')
      }
    },
    async save() {
      if (!this.validateRequiredFields()) return
      this.saving = true
      this.$emit('saving', true)
      try {
        const payload = {
          leadId: this.form.leadId,
          customerFirstName: this.form.customerFirstName,
          customerLastName: this.form.customerLastName,
          validUntil: this.form.validUntil,
          details: this.buildDetails(),
          paymentPlans: this.buildPaymentPlans()
        }
        let qid = this.quoteEditId()
        if (this.isEdit) {
          await updateQuote(qid, payload)
          await this.uploadPendingQuoteAttachments(qid)
          this.$message.success(this.$t('quoteFormPage.saved'))
        } else {
          const resp = await saveQuote(payload)
          const raw = resp && resp.data !== undefined ? resp.data : resp
          qid = Number(raw)
          if (!Number.isFinite(qid)) {
            this.$message.error(this.$t('quoteFormPage.failed'))
            return
          }
          await this.uploadPendingQuoteAttachments(qid)
          this.$message.success(this.$t('quoteFormPage.created'))
        }
        this.finishSaveNavigation()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteFormPage.failed'))
      } finally {
        this.saving = false
        this.$emit('saving', false)
      }
    },
    async submitForApproval() {
      if (!this.validateRequiredFields()) return
      this.saving = true
      this.$emit('saving', true)
      try {
        const payload = {
          leadId: this.form.leadId,
          customerFirstName: this.form.customerFirstName,
          customerLastName: this.form.customerLastName,
          validUntil: this.form.validUntil,
          details: this.buildDetails(),
          paymentPlans: this.buildPaymentPlans()
        }
        let qid = this.quoteEditId()
        if (!this.isEdit) {
          const resp = await saveQuote(payload)
          const raw = resp && resp.data !== undefined ? resp.data : resp
          qid = Number(raw)
          if (!Number.isFinite(qid)) {
            this.$message.error(this.$t('quoteFormPage.failed'))
            return
          }
        } else {
          await updateQuote(qid, payload)
        }
        await this.uploadPendingQuoteAttachments(qid)
        await submitQuote(qid)
        this.$message.success(this.$t('quoteFormPage.submittedApproval'))
        this.finishSaveNavigation()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('quoteFormPage.failed'))
      } finally {
        this.saving = false
        this.$emit('saving', false)
      }
    },
    validateRequiredFields() {
      if (!this.form.leadId) {
        this.$message.warning(this.$t('quoteFormPage.needLead'))
        return false
      }
      if (!this.form.validUntil) {
        this.$message.warning(this.$t('quoteFormPage.needValidUntil'))
        return false
      }
      return true
    }
  }
}
</script>

<style scoped>
.quote-form {
  max-width: 100%;
}

.quote-form__embed {
  min-width: 0;
}

.quote-form--embedded .el-col .el-form-item .el-input {
  width: 100%;
}

.quote-form--embedded .el-form-item .el-date-editor.el-input {
  width: 100%;
  max-width: none;
}

.quote-form__lines-toolbar--embedded {
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--color-border-soft);
}

.quote-form__lines-title--toolbar {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}

.quote-form__locked-hint {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-text-sub);
}

.quote-form__lines-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}

.quote-form__name-row {
  display: flex;
  gap: 12px;
}

.quote-form__name-row .el-input {
  flex: 1;
}

.quote-form__lines {
  margin-bottom: 8px;
}

.quote-form__lines-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.quote-form__lines-hint {
  font-size: 13px;
  color: var(--color-text-sub);
}

.quote-form__table {
  width: 100%;
}

.quote-form__attachments {
  width: 100%;
}

.quote-form__att-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
}

.quote-form__att-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 8px;
}

.quote-form__att-name {
  flex: 1;
  min-width: 120px;
}

.hidden-file {
  display: none !important;
}

.quote-form__del {
  color: #f56c6c;
}

.quote-form__contract-row {
  margin-bottom: 0;
}

.quote-form__actions {
  margin-top: 12px;
  margin-bottom: 0;
}

.quote-form__actions >>> .el-form-item__content {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.quote-form__opp-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--color-text-sub);
  line-height: 1.4;
}

.quote-form__legacy-item {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--dark-text-sub, #8b95a1);
}

.quote-form__total {
  margin: 10px 0 0;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.quote-form__plan-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #475569;
}

.quote-form__plan-unplanned {
  color: #b45309;
}

.quote-form__plan-table {
  margin-top: 4px;
}

.quote-form__product-opt {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px 0;
  max-width: 100%;
}

.quote-form__product-opt-title {
  font-weight: 500;
  color: var(--color-text, #303133);
  word-break: break-word;
  white-space: normal;
  line-height: 1.35;
}

.quote-form__product-opt-title--create {
  color: var(--color-primary, #409eff);
  font-weight: 600;
}

.quote-form__product-opt-desc {
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-sub, #909399);
  word-break: break-word;
  white-space: normal;
  line-height: 1.35;
}
</style>

<style>
/* Popper is mounted on body; align with popper-class */
.quote-form__product-select-dropdown.el-select-dropdown .el-select-dropdown__item {
  height: auto !important;
  line-height: 1.35 !important;
  padding-top: 8px;
  padding-bottom: 8px;
  max-width: min(560px, 92vw);
  white-space: normal;
}
</style>
